import { useEffect, useState } from "react";
import { api, type AdminOrder, type AdminPrices, type PriceMapping, type WaitingEvent } from "../api";
import { MIB, priceLabel, sizeLabel } from "../billing";
import { Disclosure } from "../Disclosure";
import { errorText, useT } from "../i18n";
import { formatTime } from "../time";

type Offer = AdminPrices["offers"][number];

/**
 * Admin -> Payments (PLAN.md 4.26 step 3): the payment provider in use, every price it sells with what
 * that price grants on Vrelis, the events that granted nothing yet, and the purchases. The owner maps a
 * price here, without a deploy; admins only read.
 */
export function AdminPayments({ owner }: { owner: boolean }) {
  const t = useT();
  const [prices, setPrices] = useState<AdminPrices | null>(null);
  const [events, setEvents] = useState<WaitingEvent[] | null>(null);
  const [eventsCursor, setEventsCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  /** Reads the provider and the first page of events; true when it worked (an error is shown otherwise). */
  const load = async (): Promise<boolean> => {
    setError(null);
    try {
      const [p, e] = await Promise.all([api.adminPrices(), api.waitingEvents()]);
      setPrices(p);
      setEvents(e.items);
      setEventsCursor(e.next_cursor);
      return true;
    } catch (err) {
      setError(errorText(err));
      return false;
    }
  };
  useEffect(() => { void load(); }, []);
  const moreEvents = async () => {
    try {
      const e = await api.waitingEvents(eventsCursor ?? undefined);
      setEvents((prev) => [...(prev ?? []), ...e.items]);
      setEventsCursor(e.next_cursor);
    } catch (err) {
      setError(errorText(err));
    }
  };

  async function replay(e: WaitingEvent) {
    setError(null);
    try {
      const outcome = await api.replayEvent(e.provider, e.event_id);
      // The list is read again first, so the message about this event stays on screen after it.
      if ((await load()) && outcome.status !== "processed") setError(t.adminPayments.stillWaiting(outcome.detail ?? outcome.status));
    } catch (err) {
      setError(errorText(err));
    }
  }

  if (!prices) return <>{error ? <p className="error" role="alert">{error}</p> : <p>{t.common.loading}</p>}</>;
  const p = t.adminPayments;
  const missing = [!prices.secrets.api_key && "api_key", !prices.secrets.webhook_secret && "webhook_secret", !prices.secrets.client_token && "client_token"].filter(Boolean) as string[];
  const known = new Set([...prices.offers.map((o) => o.price_id), ...prices.other_mappings.map((m) => m.price_id)]);
  // A waiting event about a price the provider no longer lists can still be mapped from here.
  const strayPrices = [...new Set((events ?? []).filter((e) => e.status === "unmapped" && e.price_id && !known.has(e.price_id)).map((e) => e.price_id!))];

  return (
    <div className="admin-payments">
      {error && <p className="error" role="alert">{error}</p>}
      <Disclosure title={p.providerTitle} open>
        <p><strong>{p.providerNames[prices.provider]}</strong> · {prices.environment === "live" ? p.live : p.sandbox}</p>
        {prices.provider === "fake" && <p className="small muted">{p.fakeNote}</p>}
        {prices.provider !== "fake" && (
          <>
            <p className="small">{missing.length === 0 ? p.keysSet : p.keysMissing(missing.map((k) => p.secretNames[k] ?? k).join(", "))}</p>
            <p className="small">{p.webhookAt} <code>{prices.webhook_url}</code></p>
          </>
        )}
        {prices.catalogue_error && <p className="error small" role="alert">{p.catalogueError(prices.catalogue_error)}</p>}
        <button type="button" className="small" onClick={() => void load()}>{p.refresh}</button>
      </Disclosure>

      <Disclosure title={p.pricesTitle(prices.offers.length)} open>
        {prices.offers.length === 0 && !prices.catalogue_error && <p className="small muted">{p.noPrices}</p>}
        <ul className="rows">
          {prices.offers.map((o) => (
            <li key={o.price_id} className="price-row">
              <span className="grow small">
                <strong>{o.name}</strong> · {priceLabel(o.amount, o.currency, t.locale)}
                {o.interval ? ` ${t.billing.every(o.interval, o.interval_count)}` : ` · ${p.oneOff}`}
                <br />
                <code>{o.price_id}</code> · {o.mapping ? <MappingLine m={o.mapping} /> : <span className="muted">{p.notSold}</span>}
              </span>
              {owner && editing !== o.price_id && <button type="button" className="link small" onClick={() => setEditing(o.price_id)}>{o.mapping ? p.edit : p.map}</button>}
              {owner && editing === o.price_id && (
                <MappingForm provider={prices.provider} priceId={o.price_id} initial={o.mapping ?? fromHint(prices.provider, o)} onDone={async () => { setEditing(null); await load(); }} onCancel={() => setEditing(null)} />
              )}
            </li>
          ))}
          {owner &&
            strayPrices.map((id) => (
              <li key={id} className="price-row">
                <span className="grow small"><code>{id}</code> · <span className="muted">{p.notListed}</span></span>
                {editing !== id ? (
                  <button type="button" className="link small" onClick={() => setEditing(id)}>{p.map}</button>
                ) : (
                  <MappingForm provider={prices.provider} priceId={id} initial={null} onDone={async () => { setEditing(null); await load(); }} onCancel={() => setEditing(null)} />
                )}
              </li>
            ))}
        </ul>
        {prices.other_mappings.length > 0 && (
          <>
            <p className="small"><strong>{p.otherMappings}</strong></p>
            <ul className="rows">
              {prices.other_mappings.map((m) => (
                <li key={m.price_id} className="price-row">
                  <span className="grow small"><code>{m.price_id}</code> · <MappingLine m={m} /></span>
                  {owner && editing !== m.price_id && <button type="button" className="link small" onClick={() => setEditing(m.price_id)}>{p.edit}</button>}
                  {owner && editing === m.price_id && <MappingForm provider={prices.provider} priceId={m.price_id} initial={m} onDone={async () => { setEditing(null); await load(); }} onCancel={() => setEditing(null)} />}
                </li>
              ))}
            </ul>
          </>
        )}
        {!owner && <p className="small muted">{p.ownerOnly}</p>}
      </Disclosure>

      <Disclosure title={p.eventsTitle(events?.length ?? 0)} open={(events?.length ?? 0) > 0}>
        {events && events.length === 0 && <p className="small muted">{p.noEvents}</p>}
        <ul className="rows">
          {events?.map((e) => (
            <li key={`${e.provider}:${e.event_id}`}>
              <span className="grow small">
                {e.status === "unmapped" ? p.unmapped : e.status === "flagged" ? p.flagged : e.status === "invalid" ? p.invalid : p.unmatched} · {e.type} · {formatTime(e.occurred_at * 1000)}
                <br />
                <span className="muted">{e.member_email ?? p.noMember}{e.price_id ? <> · <code>{e.price_id}</code></> : null}{e.detail ? ` · ${e.detail}` : ""}</span>
                <br />
                <code className="muted">{e.provider}:{e.event_id}</code>
              </span>
              {owner && (e.status === "unmapped" || e.status === "unmatched") && <button type="button" className="link small" onClick={() => void replay(e)}>{p.replay}</button>}
            </li>
          ))}
        </ul>
        {eventsCursor && <button type="button" className="small" onClick={() => void moreEvents()}>{t.common.more}</button>}
        <p className="small muted">{p.eventsNote}</p>
      </Disclosure>

      <Purchases />
    </div>
  );
}

function MappingLine({ m }: { m: PriceMapping }) {
  const t = useT();
  const text = m.kind === "subscription" ? t.adminPayments.grantsPlan(t.billing.planNames[m.plan_id ?? ""] ?? String(m.plan_id)) : t.billing.packDetail(m.stamps, m.stamp_months, sizeLabel(m.extra_storage_bytes), sizeLabel(m.attachment_bytes), m.attachment_months);
  return <span>{text}{m.active ? "" : ` · ${t.adminPayments.inactive}`}</span>;
}

/** A first guess for the form from the provider's own data on the price (Paddle `custom_data`, Dodo `metadata`). */
function fromHint(provider: PriceMapping["provider"], o: Offer): PriceMapping | null {
  const c = o.custom_data ?? {};
  const n = (v: unknown) => (typeof v === "number" ? v : typeof v === "string" && /^[0-9]+$/.test(v) ? Number(v) : 0);
  const kind = c.kind === "pack" || c.kind === "subscription" ? c.kind : o.interval ? "subscription" : "pack";
  const plan = c.plan_id === "legacy" ? "legacy" : "start";
  return {
    provider,
    price_id: o.price_id,
    kind,
    plan_id: kind === "subscription" ? plan : null,
    stamps: n(c.stamps),
    stamp_months: n(c.stamp_months),
    extra_storage_bytes: n(c.extra_storage_bytes),
    attachment_bytes: n(c.attachment_bytes),
    attachment_months: n(c.attachment_months),
    active: 1,
  };
}

/** The owner's form: what one provider price grants. Sizes are entered in MiB. */
function MappingForm({ provider, priceId, initial, onDone, onCancel }: { provider: PriceMapping["provider"]; priceId: string; initial: PriceMapping | null; onDone: () => Promise<void>; onCancel: () => void }) {
  const t = useT();
  const p = t.adminPayments;
  const [kind, setKind] = useState<PriceMapping["kind"]>(initial?.kind ?? "pack");
  const [plan, setPlan] = useState<"start" | "legacy">(initial?.plan_id === "legacy" ? "legacy" : "start");
  const num = (v: number | undefined) => (v ? String(v) : "");
  const [stamps, setStamps] = useState(num(initial?.stamps));
  const [stampMonths, setStampMonths] = useState(num(initial?.stamp_months));
  const [storageMib, setStorageMib] = useState(num(initial ? initial.extra_storage_bytes / MIB : 0));
  const [fileMib, setFileMib] = useState(num(initial ? initial.attachment_bytes / MIB : 0));
  const [fileMonths, setFileMonths] = useState(num(initial?.attachment_months));
  const [active, setActive] = useState(initial ? initial.active === 1 : true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const whole = (v: string) => (v.trim() === "" ? 0 : Math.round(Number(v)));

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.savePrice(
        kind === "subscription"
          ? { provider, price_id: priceId, kind, plan_id: plan, stamps: 0, stamp_months: 0, extra_storage_bytes: 0, attachment_bytes: 0, attachment_months: 0, active }
          : { provider, price_id: priceId, kind, plan_id: null, stamps: whole(stamps), stamp_months: whole(stampMonths), extra_storage_bytes: whole(storageMib) * MIB, attachment_bytes: whole(fileMib) * MIB, attachment_months: whole(fileMonths), active },
      );
      await onDone();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <form className="small mapping-form" onSubmit={(e) => void save(e)} aria-label={p.formLabel(priceId)}>
      <div className="form-row">
        <label>{p.kind}<select value={kind} onChange={(e) => setKind(e.target.value as PriceMapping["kind"])}><option value="subscription">{p.kindSubscription}</option><option value="pack">{p.kindPack}</option></select></label>
        {kind === "subscription" && <label>{t.adminBilling.plan}<select value={plan} onChange={(e) => setPlan(e.target.value as "start" | "legacy")}>{(["start", "legacy"] as const).map((id) => <option key={id} value={id}>{t.billing.planNames[id]}</option>)}</select></label>}
      </div>
      {kind === "pack" && (
        <div className="form-row">
          <label>{p.stamps}<input type="number" min={0} max={100000} value={stamps} onChange={(e) => setStamps(e.target.value)} /></label>
          <label>{p.stampMonths}<input type="number" min={0} max={120} value={stampMonths} onChange={(e) => setStampMonths(e.target.value)} /></label>
          <label>{p.storageMib}<input type="number" min={0} value={storageMib} onChange={(e) => setStorageMib(e.target.value)} /></label>
          <label>{p.fileMib}<input type="number" min={0} value={fileMib} onChange={(e) => setFileMib(e.target.value)} /></label>
          <label>{p.fileMonths}<input type="number" min={0} max={120} value={fileMonths} onChange={(e) => setFileMonths(e.target.value)} /></label>
        </div>
      )}
      <label className="check"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {p.active}</label>
      <div className="form-row">
        <button type="submit" disabled={busy}>{p.save}</button>
        <button type="button" className="link" onClick={onCancel}>{t.common.cancel}</button>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
    </form>
  );
}

/** Purchases at the provider, newest first: started (open) and completed by its event. */
function Purchases() {
  const t = useT();
  const p = t.adminPayments;
  const [status, setStatus] = useState<"completed" | "open" | "all">("completed");
  const [list, setList] = useState<AdminOrder[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = async (more: boolean) => {
    try {
      const page = await api.adminOrders(status, more ? (cursor ?? undefined) : undefined);
      setList((prev) => (more && prev ? [...prev, ...page.items] : page.items));
      setCursor(page.next_cursor);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  };
  useEffect(() => { void load(false); }, [status]);
  return (
    <Disclosure title={p.purchasesTitle}>
      <div className="chips">
        {(["completed", "open", "all"] as const).map((s) => <button key={s} type="button" className={status === s ? "chip on" : "chip"} onClick={() => setStatus(s)}>{p.purchaseFilter[s]}</button>)}
      </div>
      {list && list.length === 0 && <p className="small muted">{p.noPurchases}</p>}
      <ul className="rows">
        {list?.map((o) => (
          <li key={o.id} className="small">
            <span className="grow">
              {o.member_email} · <code>{o.price_id}</code> · {t.billing.orderStatus[o.status] ?? o.status} · {formatTime((o.completed_at ?? o.created_at) * 1000)}
              {(o.provider_transaction_id || o.provider_subscription_id) && <><br /><code className="muted">{[o.provider_transaction_id, o.provider_subscription_id].filter(Boolean).join(" · ")}</code></>}
            </span>
          </li>
        ))}
      </ul>
      {cursor && <button type="button" className="small" onClick={() => void load(true)}>{t.common.more}</button>}
      {error && <p className="error small" role="alert">{error}</p>}
    </Disclosure>
  );
}
